import tokens from '@qkindex/design-tokens';
import { alpha, createTheme, Theme } from '@mui/material/styles';
import { createElement } from 'react';
import { dialogPaperSurface, surfaceShadows } from './surfaceStyles';
import { minimumInteractiveSize } from './components/ui/controlSizing';
import {
  CheckboxCheckedIcon,
  CheckboxIcon,
  CheckboxIndeterminateIcon,
  ChevronDownIcon,
  CloseIcon,
  CONTROL_ICON_SIZE,
  ErrorIcon,
  InfoIcon,
  SuccessIcon,
  WarningIcon,
} from './components/ui/icons';

const primary = tokens.color.primary;
const ink = tokens.color.ink;
const muted = tokens.color.muted;
const border = tokens.color.border;
const canvas = tokens.color.canvas;
const borderRadius = tokens.radius;
const keyboardFocusRing = tokens.focusRing;
const hoverOpacity = tokens.hoverOpacity;
const selectedOpacity = tokens.selectedOpacity;
const controlThumbShadow =
  '0 1px 3px rgba(36, 40, 36, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.85)';
const controlTrackShadow =
  'inset 1px 2px 3px rgba(36, 40, 36, 0.28), inset 0 -1px 1px rgba(255, 255, 255, 0.35)';
const controlTrackColor = '#d7dbd0';

// Light controls share an olive tint; destructive controls retain their red cue.
const buttonHover = (theme: Theme, color?: string) => ({
  '@media (hover: hover)': {
    '&:hover:not(.Mui-disabled)': {
      backgroundColor: alpha(
        color === 'error'
          ? theme.palette.error.main
          : theme.palette.primary.main,
        hoverOpacity,
      ),
    },
  },
});
const selectedButtonHover = (theme: Theme, color?: string) => ({
  ...buttonHover(theme, color),
  '&.Mui-selected': {
    color:
      color === 'error' ? theme.palette.error.main : theme.palette.primary.main,
    backgroundColor: alpha(
      color === 'error' ? theme.palette.error.main : theme.palette.primary.main,
      selectedOpacity,
    ),
    boxShadow: surfaceShadows.inset,
    '@media (hover: hover)': {
      '&:hover:not(.Mui-disabled)': {
        backgroundColor: alpha(
          color === 'error'
            ? theme.palette.error.main
            : theme.palette.primary.main,
          selectedOpacity + hoverOpacity,
        ),
      },
    },
  },
});

export const theme = createTheme({
  palette: {
    primary: {
      main: primary,
      dark: tokens.color.primaryDark,
      light: tokens.color.primaryLight,
      contrastText: '#ffffff',
    },
    secondary: { main: '#596455', contrastText: '#ffffff' },
    info: {
      main: primary,
      light: '#e9ede3',
      dark: tokens.color.primaryDark,
      contrastText: '#ffffff',
    },
    success: {
      main: '#4f6b45',
      light: '#e7eee2',
      dark: '#344a2d',
      contrastText: '#ffffff',
    },
    warning: {
      main: '#88651f',
      light: '#f3ead5',
      dark: '#654a17',
      contrastText: '#ffffff',
    },
    error: {
      main: '#a04b43',
      light: '#f3e4e1',
      dark: '#783730',
      contrastText: '#ffffff',
    },
    background: { default: canvas, paper: canvas },
    text: { primary: ink, secondary: muted },
    divider: border,
    action: {
      hover: alpha(primary, hoverOpacity),
      hoverOpacity,
      selectedOpacity,
      selected: alpha(primary, selectedOpacity),
      focus: alpha(primary, 0.14),
      disabledBackground: '#eeeee8',
    },
  },
  shape: { borderRadius },
  spacing: tokens.spacing,
  typography: {
    fontFamily: tokens.font.family,
    h1: {
      fontWeight: tokens.font.headingWeight,
      letterSpacing: tokens.font.headingTracking,
    },
    h2: { fontWeight: 700, letterSpacing: '-0.03em' },
    h3: { fontWeight: 700, letterSpacing: '-0.025em' },
    h4: { fontWeight: 700, letterSpacing: '-0.025em' },
    h5: { fontWeight: 700, letterSpacing: '-0.02em' },
    h6: { fontSize: '1.125rem', fontWeight: 700, letterSpacing: '-0.015em' },
    subtitle1: { fontWeight: 600 },
    body1: {
      fontSize: tokens.font.bodySize,
      lineHeight: tokens.font.bodyLineHeight,
    },
    body2: { fontSize: '0.875rem', lineHeight: 1.5 },
    caption: { lineHeight: 1.5 },
    button: {
      fontWeight: tokens.font.buttonWeight,
      textTransform: 'none',
      letterSpacing: 0,
    },
  },
  components: {
    MuiFormControl: { defaultProps: { size: 'small' } },
    MuiSelect: { defaultProps: { IconComponent: ChevronDownIcon } },
    MuiNativeSelect: { defaultProps: { IconComponent: ChevronDownIcon } },
    MuiCheckbox: {
      defaultProps: {
        icon: createElement(CheckboxIcon),
        checkedIcon: createElement(CheckboxCheckedIcon),
        indeterminateIcon: createElement(CheckboxIndeterminateIcon),
      },
      styleOverrides: {
        root: {
          minWidth: minimumInteractiveSize,
          minHeight: minimumInteractiveSize,
          padding: 12,
          borderRadius: 10,
          '& > .MuiSvgIcon-root': {
            backgroundColor: canvas,
            borderRadius: 5,
            boxShadow: surfaceShadows.control,
          },
          '&.Mui-checked > .MuiSvgIcon-root, &.MuiCheckbox-indeterminate > .MuiSvgIcon-root':
            {
              backgroundColor: '#e6e9df',
              boxShadow: surfaceShadows.field,
            },
          '&.Mui-disabled > .MuiSvgIcon-root': {
            backgroundColor: '#eeeee8',
            boxShadow: 'none',
          },
          // Keep the ring inside corner overlays and clipped list rows.
          '&.Mui-focusVisible': { outlineOffset: -2 },
          '& input:focus-visible': { outline: 'none' },
          '@media (forced-colors: active)': {
            '& > .MuiSvgIcon-root': {
              backgroundColor: 'Canvas',
              color: 'CanvasText',
              boxShadow: 'none',
            },
            '&.Mui-checked > .MuiSvgIcon-root, &.MuiCheckbox-indeterminate > .MuiSvgIcon-root':
              {
                backgroundColor: 'Canvas',
                color: 'Highlight',
              },
            '&.Mui-disabled > .MuiSvgIcon-root': { color: 'GrayText' },
          },
        },
      },
    },
    MuiSwitch: {
      defaultProps: { disableRipple: true },
      styleOverrides: {
        root: ({ ownerState }) => ({
          width: ownerState.size === 'small' ? 56 : 60,
          height: minimumInteractiveSize,
          minHeight: minimumInteractiveSize,
          padding: 10,
          // Contain MUI's 300%-wide input, including when the thumb moves right.
          overflow: 'hidden',
          // Override MUI's compact-size descendant rules as well as the base slot.
          '& .MuiSwitch-switchBase': {
            padding: ownerState.size === 'small' ? 14 : 13,
            '&.Mui-checked': {
              transform: `translateX(${ownerState.size === 'small' ? 12 : 16}px)`,
            },
          },
        }),
        switchBase: ({ ownerState }) => ({
          padding: ownerState.size === 'small' ? 14 : 13,
          color: canvas,
          // Hover feedback belongs on the thumb, not the invisible 44px target.
          '&:hover, &.Mui-checked:hover': { backgroundColor: 'transparent' },
          '@media (hover: hover)': {
            '&:hover:not(.Mui-disabled) .MuiSwitch-thumb': {
              backgroundColor: '#ffffff',
            },
          },
          '&.Mui-checked': {
            transform: `translateX(${ownerState.size === 'small' ? 12 : 16}px)`,
            color: canvas,
            '& + .MuiSwitch-track': { backgroundColor: primary, opacity: 1 },
          },
          '&.Mui-disabled, &.Mui-checked.Mui-disabled': {
            color: '#eeeee8',
            '& .MuiSwitch-thumb': { boxShadow: 'none', borderColor: border },
          },
          '&.Mui-disabled + .MuiSwitch-track': { opacity: 0.5 },
          '&.Mui-focusVisible': {
            outline: 'none',
            '& .MuiSwitch-thumb': {
              outline: keyboardFocusRing,
              outlineOffset: 2,
            },
          },
          '& input:focus-visible': { outline: 'none' },
          '@media (forced-colors: active)': {
            '&, &.Mui-checked': { color: 'ButtonFace' },
            '&.Mui-checked + .MuiSwitch-track': {
              backgroundColor: 'Highlight',
            },
            '&.Mui-disabled .MuiSwitch-thumb': { borderColor: 'GrayText' },
            '&.Mui-focusVisible .MuiSwitch-thumb': {
              outlineColor: 'Highlight',
            },
          },
        }),
        thumb: ({ ownerState }) => ({
          width: ownerState.size === 'small' ? 16 : 18,
          height: ownerState.size === 'small' ? 16 : 18,
          boxSizing: 'border-box',
          border: `1px solid ${alpha(primary, 0.18)}`,
          // A local drop shadow keeps the thumb separate from the recessed track.
          // The shared control shadow's white halo overwhelms this small surface.
          boxShadow: controlThumbShadow,
          '@media (forced-colors: active)': {
            borderColor: 'ButtonText',
            boxShadow: 'none',
          },
        }),
        track: {
          borderRadius: 12,
          backgroundColor: controlTrackColor,
          opacity: 1,
          boxShadow: controlTrackShadow,
          '@media (forced-colors: active)': {
            backgroundColor: 'Canvas',
            border: '1px solid ButtonText',
            boxShadow: 'none',
          },
        },
      },
    },
    MuiSlider: {
      styleOverrides: {
        root: ({ ownerState }) =>
          ownerState.orientation !== 'vertical'
            ? {
                boxSizing: 'border-box',
                minHeight: minimumInteractiveSize,
                paddingBlock: 16,
              }
            : {},
        rail: ({ ownerState }) => ({
          ...(ownerState.orientation === 'vertical'
            ? { width: 12 }
            : { height: 12 }),
          backgroundColor: controlTrackColor,
          opacity: 1,
          boxShadow: controlTrackShadow,
          '.Mui-disabled &': { opacity: 0.5, boxShadow: 'none' },
          '@media (forced-colors: active)': {
            backgroundColor: 'Canvas',
            border: '1px solid ButtonText',
            boxShadow: 'none',
          },
        }),
        track: ({ ownerState }) => ({
          ...(ownerState.orientation === 'vertical'
            ? { width: 12 }
            : { height: 12 }),
          border: 0,
          boxShadow: controlTrackShadow,
          '.Mui-disabled &': { opacity: 0.5, boxShadow: 'none' },
          '@media (forced-colors: active)': {
            backgroundColor: 'Highlight',
            boxShadow: 'none',
          },
        }),
        thumb: {
          width: 22,
          height: 22,
          backgroundColor: canvas,
          border: `1px solid ${alpha(primary, 0.18)}`,
          boxShadow: controlThumbShadow,
          '&::before': { boxShadow: 'none' },
          '&:hover, &.Mui-focusVisible, &.Mui-active': {
            boxShadow: controlThumbShadow,
          },
          '@media (hover: hover)': {
            '&:hover': { backgroundColor: '#ffffff' },
          },
          '&.Mui-focusVisible': {
            outline: keyboardFocusRing,
            outlineOffset: 2,
          },
          '&.Mui-disabled': {
            backgroundColor: '#eeeee8',
            borderColor: border,
            boxShadow: 'none',
          },
          '& input:focus-visible': { outline: 'none' },
          '@media (forced-colors: active)': {
            backgroundColor: 'ButtonFace',
            borderColor: 'ButtonText',
            '&, &:hover, &.Mui-focusVisible, &.Mui-active': {
              boxShadow: 'none',
            },
            '&.Mui-focusVisible': { outlineColor: 'Highlight' },
            '&.Mui-disabled': {
              backgroundColor: 'ButtonFace',
              borderColor: 'GrayText',
            },
          },
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        '*, *::before, *::after': { boxSizing: 'border-box' },
        html: { minHeight: '100%', backgroundColor: canvas },
        body: {
          minHeight: '100%',
          margin: 0,
          overflowX: 'hidden',
          WebkitFontSmoothing: 'antialiased',
        },
        '#root': { minHeight: '100%' },
        ':focus-visible': { outline: keyboardFocusRing, outlineOffset: 2 },
        '@media (forced-colors: active)': {
          ':focus-visible, .MuiButtonBase-root.Mui-focusVisible, .MuiOutlinedInput-root.Mui-focused':
            {
              outline: '2px solid Highlight',
              outlineOffset: 2,
            },
        },
        '@media (prefers-reduced-motion: reduce)': {
          '*, *::before, *::after': {
            animationDuration: '0.01ms !important',
            animationIterationCount: '1 !important',
            transitionDuration: '0.01ms !important',
            scrollBehavior: 'auto !important',
          },
        },
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          '&.Mui-focusVisible': {
            outline: keyboardFocusRing,
            outlineOffset: 2,
          },
          '@media (forced-colors: active)': {
            '&.Mui-focusVisible': { outlineColor: 'Highlight' },
          },
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          minHeight: minimumInteractiveSize,
          borderRadius,
          paddingInline: 18,
        },
        outlined: ({ theme, ownerState }) => ({
          ...buttonHover(theme, ownerState.color),
          border: 0,
          backgroundColor: canvas,
          boxShadow: surfaceShadows.control,
          '&:hover': { border: 0, boxShadow: surfaceShadows.control },
          '&:active': { boxShadow: surfaceShadows.inset },
          '&.Mui-disabled': { border: 0, boxShadow: 'none' },
        }),
        text: ({ theme, ownerState }) => ({
          paddingInline: 12,
          ...buttonHover(theme, ownerState.color),
        }),
        // Let a surface override its resting shadow without duplicating hover rules.
        contained: {
          boxShadow: `var(--button-shadow, ${surfaceShadows.primary})`,
          '&:hover': {
            boxShadow: `var(--button-shadow, ${surfaceShadows.primary})`,
          },
          '&:active': { boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.18)' },
          '&.Mui-disabled': { boxShadow: 'none' },
        },
        startIcon: {
          '& > .MuiSvgIcon-root:nth-of-type(1)': {
            fontSize: CONTROL_ICON_SIZE,
          },
        },
        endIcon: {
          '& > .MuiSvgIcon-root:nth-of-type(1)': {
            fontSize: CONTROL_ICON_SIZE,
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: ({ theme, ownerState }) => ({
          ...buttonHover(theme, ownerState.color),
          minWidth: minimumInteractiveSize,
          minHeight: minimumInteractiveSize,
          borderRadius,
          color: ownerState.color === 'default' ? muted : undefined,
          boxShadow: surfaceShadows.control,
          '&.Mui-disabled': { boxShadow: 'none' },
        }),
      },
    },
    MuiFab: {
      styleOverrides: {
        root: {
          minWidth: 56,
          minHeight: 56,
          borderRadius,
          boxShadow: surfaceShadows.primary,
          '&:hover': { boxShadow: surfaceShadows.primary },
        },
      },
    },
    MuiTextField: { defaultProps: { variant: 'outlined', size: 'small' } },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          minHeight: minimumInteractiveSize,
          borderRadius,
          backgroundColor: canvas,
          boxShadow: surfaceShadows.field,
          '& .MuiOutlinedInput-notchedOutline, &:hover .MuiOutlinedInput-notchedOutline, &.Mui-focused .MuiOutlinedInput-notchedOutline, &.Mui-error .MuiOutlinedInput-notchedOutline, &.Mui-disabled .MuiOutlinedInput-notchedOutline':
            { border: 0 },
          '&.Mui-focused': {
            outline: `1px solid ${alpha(primary, 0.35)}`,
            outlineOffset: 2,
            boxShadow: `${surfaceShadows.field}, 0 0 0 3px ${alpha(primary, 0.08)}`,
          },
          '&:has(:focus-visible)': {
            outline: keyboardFocusRing,
            outlineOffset: 2,
          },
          // The outer surface owns the ring, including multiline and select
          // fields, so their inner native controls don't draw a second ring.
          '& .MuiInputBase-input:focus-visible': { outline: 'none' },
          '&.Mui-error': {
            outline: '2px solid',
            outlineColor: '#d32f2f',
            outlineOffset: 2,
          },
          '@media (forced-colors: active)': {
            '&.Mui-focused, &:has(:focus-visible)': {
              outline: '2px solid Highlight',
              outlineOffset: 2,
            },
          },
        },
        notchedOutline: { border: 0 },
      },
    },
    MuiInputLabel: {
      defaultProps: { shrink: true, disableAnimation: true },
      styleOverrides: {
        root: {
          position: 'static',
          transform: 'none',
          maxWidth: '100%',
          whiteSpace: 'normal',
          overflowWrap: 'anywhere',
          marginBottom: 8,
          fontSize: '0.875rem',
          lineHeight: 1.5,
          fontWeight: 600,
          color: ink,
          pointerEvents: 'auto',
          '&.MuiInputLabel-shrink': { transform: 'none', maxWidth: '100%' },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
        outlined: { border: 0, boxShadow: surfaceShadows.control },
      },
    },
    MuiDialog: {
      defaultProps: { scroll: 'paper' },
      styleOverrides: { paper: dialogPaperSurface },
    },
    MuiDialogTitle: {
      styleOverrides: { root: { fontWeight: 700, letterSpacing: '-0.02em' } },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: alpha('#28241e', 0.24),
          '&:not(.MuiBackdrop-invisible)': {
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          border: 0,
          borderRadius,
          boxShadow: `0 8px 30px ${alpha(ink, 0.12)}`,
        },
        list: { padding: 6 },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: { minHeight: 44, borderRadius, fontSize: '0.875rem' },
      },
    },
    MuiListItemIcon: { styleOverrides: { root: { color: muted } } },
    MuiChip: {
      defaultProps: { deleteIcon: createElement(CloseIcon) },
      styleOverrides: {
        root: ({ ownerState }) => ({
          fontWeight: 500,
          ...(ownerState.clickable || ownerState.onDelete
            ? { minHeight: minimumInteractiveSize, height: 'auto' }
            : {}),
          '&.MuiChip-filled.MuiChip-colorDefault': {
            backgroundColor: '#f0f1ec',
            color: muted,
          },
        }),
        outlined: { border: 0, boxShadow: surfaceShadows.control },
        deleteIcon: {
          fontSize: 20,
          minWidth: minimumInteractiveSize,
          minHeight: minimumInteractiveSize,
          boxSizing: 'border-box',
          padding: 12,
        },
        deleteIconSmall: { fontSize: 16, padding: 14 },
      },
    },
    MuiTabs: { styleOverrides: { indicator: { display: 'none' } } },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          ...selectedButtonHover(theme, undefined),
          fontWeight: 600,
          color: muted,
          borderRadius,
          boxShadow: surfaceShadows.control,
          '&.Mui-disabled': { boxShadow: 'none' },
        }),
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: ({ theme, ownerState }) => ({
          ...selectedButtonHover(theme, ownerState.color),
          minWidth: minimumInteractiveSize,
          minHeight: minimumInteractiveSize,
          border: 0,
          borderRadius,
          color: muted,
          boxShadow: surfaceShadows.control,
          '&.Mui-disabled': { boxShadow: 'none' },
        }),
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          boxShadow: surfaceShadows.raised,
          border: 0,
          '&::before': { display: 'none' },
        },
      },
    },
    MuiAlert: {
      defaultProps: {
        iconMapping: {
          success: createElement(SuccessIcon),
          warning: createElement(WarningIcon),
          error: createElement(ErrorIcon),
          info: createElement(InfoIcon),
        },
        slots: { closeIcon: CloseIcon },
      },
      styleOverrides: {
        root: { borderRadius, alignItems: 'center' },
        standard: ({ theme, ownerState }) => {
          const severity = ownerState.severity || 'success';
          const palette = theme.palette[ownerState.color || severity];
          return {
            backgroundColor: palette.light,
            color: ink,
            '& .MuiAlert-icon': { color: palette.main, opacity: 1 },
          };
        },
        message: { fontSize: '0.875rem' },
      },
    },
    MuiTooltip: { defaultProps: { arrow: true } },
    MuiCard: {
      styleOverrides: {
        root: { border: 0, borderRadius, boxShadow: surfaceShadows.raised },
      },
    },
  },
});
